import mermaid from 'mermaid'
import { useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rawContent from '../../../../docs/plans/new-payment-system-plan.md?raw'

mermaid.initialize({ startOnLoad: false, theme: 'default' })

function MermaidBlock({ code }: { code: string }): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!ref.current) return
    const id = `mermaid-${Math.random().toString(36).slice(2)}`
    mermaid
      .render(id, code)
      .then(({ svg }) => {
        if (ref.current) ref.current.innerHTML = svg
      })
      .catch((err) => {
        if (ref.current)
          ref.current.textContent = `Diagram error: ${err.message}`
      })
  }, [code])

  return <div ref={ref} className="my-4 overflow-x-auto [&>svg]:mx-0" />
}

export default function NewPaymentSystemPlanPage(): React.ReactElement {
  return (
    <div className="text-left">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Mermaid fenced code blocks
          code({ className, children, ...props }) {
            const match = /language-mermaid/.test(className ?? '')
            if (match) {
              return <MermaidBlock code={String(children).trim()} />
            }
            return (
              <code
                className={`rounded bg-gray-100 px-1 font-mono text-sm ${className ?? ''}`}
                {...props}
              >
                {children}
              </code>
            )
          },
          // Fenced code blocks (non-mermaid)
          pre({ children }) {
            return (
              <pre className="my-4 overflow-x-auto rounded-lg bg-gray-100 p-4 font-mono text-sm">
                {children}
              </pre>
            )
          },
          h1({ children }) {
            return (
              <h1 className="mt-8 mb-4 font-bold text-3xl text-gray-900">
                {children}
              </h1>
            )
          },
          h2({ children }) {
            return (
              <h2 className="mt-6 mb-3 font-semibold text-2xl text-gray-800">
                {children}
              </h2>
            )
          },
          h3({ children }) {
            return (
              <h3 className="mt-4 mb-2 font-semibold text-gray-800 text-xl">
                {children}
              </h3>
            )
          },
          p({ children }) {
            return (
              <p className="mb-3 text-gray-700 leading-relaxed">{children}</p>
            )
          },
          ul({ children }) {
            return (
              <ul className="mb-3 list-disc space-y-1 pl-6 text-gray-700">
                {children}
              </ul>
            )
          },
          ol({ children }) {
            return (
              <ol className="mb-3 list-decimal space-y-1 pl-6 text-gray-700">
                {children}
              </ol>
            )
          },
          li({ children }) {
            return <li className="leading-relaxed">{children}</li>
          },
          table({ children }) {
            return (
              <div className="my-4 overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 rounded-lg border border-gray-200 text-sm">
                  {children}
                </table>
              </div>
            )
          },
          thead({ children }) {
            return <thead className="bg-gray-50">{children}</thead>
          },
          th({ children }) {
            return (
              <th className="px-4 py-2 text-left font-semibold text-gray-700 text-xs uppercase tracking-wider">
                {children}
              </th>
            )
          },
          td({ children }) {
            return (
              <td className="border-gray-100 border-t px-4 py-2 text-gray-700">
                {children}
              </td>
            )
          },
          blockquote({ children }) {
            return (
              <blockquote className="my-3 border-blue-400 border-l-4 pl-4 text-gray-600 italic">
                {children}
              </blockquote>
            )
          },
          hr() {
            return <hr className="my-6 border-gray-200" />
          },
          a({ href, children }) {
            return (
              <a
                href={href}
                className="text-blue-600 underline hover:text-blue-800"
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </a>
            )
          },
        }}
      >
        {rawContent}
      </ReactMarkdown>
    </div>
  )
}
